import {DefaultEquality, Equality, Pair, PairOf, Transform} from "../../../types/GenericTypes";
import {isNotNull} from "../../util/UtilFunctions";

type HashFunction<K, V> = (key: K) => V;
type StringHashFunction<K> = HashFunction<K, string>;
type NumberHashFunction<K> = HashFunction<K, number>;

/**
 * Not quite a real HashMap as we are creating a map where we are hashing the keys not the values,
 * this is for times where we want to use an object as the key for a map but can't because JS doesn't work like that
 */
export class HashKeyMap<K, V> {

    /*
     * We lose the proper typing for the map key here as there isn't a nice way to express the key type without
     * also exposing it as part of the generics, which isn't useful to the consumer in any way.
     * Thus we have to simply accept that the keys will always be correct.
     */
    private constructor(private readonly hashFunction: HashFunction<K, any>,
                        private readonly valueMap: Map<any, V> = new Map(),
                        private readonly hashMap: Map<any, K> = new Map()) {
    }

    public get(key: K): V | null {
        const hash = this.hashFunction(key);
        return this.valueMap.get(hash) ?? null;
    }

    public getOrDefault(key: K, defaultValue: V): V {
        return this.get(key) ?? defaultValue;
    }

    public set(key: K, value: V): HashKeyMap<K, V> {
        const hash = this.hashFunction(key);
        this.hashMap.set(hash, key);
        this.valueMap.set(hash, value);
        return this;
    }

    public delete(key: K): HashKeyMap<K, V> {
        const hash = this.hashFunction(key);
        this.hashMap.delete(hash);
        this.valueMap.delete(hash);
        return this;
    }

    public computeIfAbsent(key: K, fn: Transform<K, V>): V {
        const hash = this.hashFunction(key);
        if (this.hashMap.has(hash)) {
            return this.valueMap.get(hash)!;
        } else {
            const value = fn(key);
            this.hashMap.set(hash, key);
            this.valueMap.set(hash, value);
            return value;
        }
    }

    public keys(): ReadonlyArray<K> {
        return Array.from(this.hashMap.values());
    }

    public values(): ReadonlyArray<V> {
        return Array.from(this.valueMap.values());
    }

    public entries(): ReadonlyArray<Pair<K, V>> {
        return this.keys().map((k) => {
            const value = this.get(k);
            if (value == null) {
                return null;
            } else {
                return PairOf(k, value);
            }
        }).filter(isNotNull);
    }

    public size(): number {
        return this.valueMap.size;
    }

    public has(key: K): boolean {
        const hash = this.hashFunction(key);
        return this.hashMap.has(hash);
    }

    public clear() {
        this.hashMap.clear();
        this.valueMap.clear();
    }

    public copy(): HashKeyMap<K, V> {
        const values = new Map<any, V>();
        this.valueMap.forEach((value, key) => values.set(key, value));
        const hashes = new Map<any, K>();
        this.hashMap.forEach((value, key) => hashes.set(key, value));
        return new HashKeyMap(this.hashFunction, values, hashes);
    }

    public isSameAs(other: HashKeyMap<K, V>, itemEquality: Equality<V> = DefaultEquality()): boolean {
        if (this.size() === 0) {
            return other.size() === 0;
        }
        return this.entries().every(([key, value]) => {
            return other.has(key) && (itemEquality(value, other.get(key)!));
        });
    }

    public static of<K, V = K>(hashFunction: StringHashFunction<K> | NumberHashFunction<K>): HashKeyMap<K, V> {
        return new HashKeyMap<K, V>(hashFunction);
    }
}
